<div class="flex flex-col gap-4">
  <div class="flex items-center justify-between">
    <div class="flex items-center gap-4">
      <h2 class="text-xl font-medium leading-8 tracking-wide text-gray-900 m-0">
        Routines
      </h2>
      @if (selectedVersionId() || currentVersion || appStateService.version()) {
        <div class="version-badge-container">
          <span
            class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs version-badge"
          >
            {{ getCurrentVersionDisplayName() }}
          </span>
          @if (projectVersions().length > 1 && !showVersionSelector()) {
            <button
              mat-icon-button
              (click)="changeVersion()"
              matTooltip="Change version"
              class="change-version-button"
            >
              <mat-icon class="text-sm">swap_horiz</mat-icon>
            </button>
          }
        </div>
      }
    </div>
    @if (canCreatePlans) {
      <button
        mat-button
        class="add-button"
        (click)="onAddNew()"
        [disabled]="showVersionSelector()"
        matTooltip="Create a new routine"
      >
        <mat-icon>add_circle</mat-icon>
        <span>Add New</span>
      </button>
    }
    @if (!canCreatePlans) {
      <div
        class="permission-message"
        matTooltip="Only project owners and administrators can create routines"
      >
        <mat-icon class="text-gray-400">info</mat-icon>
        <span class="text-gray-500 text-sm"
          >Only owners can create routines</span
        >
      </div>
    }
  </div>

  <!-- Version Selector -->
  @if (showVersionSelector()) {
    <div class="version-selector">
      <mat-card
        class="border border-blue-200 rounded-lg shadow-sm overflow-hidden p-0"
      >
        <div class="flex flex-col justify-center items-center py-16 gap-4">
          <mat-icon class="text-blue-500 text-6xl">account_tree</mat-icon>
          <h3 class="text-blue-700 text-lg font-medium m-0">
            {{
              projectVersions().length > 0
                ? "Select a Version"
                : "No Versions Available"
            }}
          </h3>
          <p class="text-blue-600 text-sm m-0 text-center max-w-sm">
            {{
              projectVersions().length > 0
                ? "Please select a version to create routines for this project."
                : "Create a version to start managing routines for this project."
            }}
          </p>
          <!-- Version Selection -->
          <div class="version-selection-container">
            @if (projectVersions().length > 0) {
              <mat-form-field class="w-64">
                <mat-label>Select Version</mat-label>
                <mat-select
                  [value]="selectedVersionId()"
                  (selectionChange)="onVersionSelected($event.value)"
                >
                  @for (version of projectVersions(); track version.versionId) {
                    <mat-option [value]="version.versionId">
                      {{ getVersionDisplayName(version) }} -
                      {{ version.createdAt | date: "short" }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            <!-- Create New Version Button -->
            <button
              mat-raised-button
              color="primary"
              (click)="openVersionDialog()"
              class="create-version-button"
            >
              <mat-icon>add</mat-icon>
              <span>{{
                projectVersions().length > 0
                  ? "Create New Version"
                  : "Create First Version"
              }}</span>
            </button>
          </div>
        </div>
      </mat-card>
    </div>
  }

  <!-- Shared Form Template -->
  @if (showAddForm() || showEditForm()) {
    <div [class]="showAddForm() ? 'inline-add-form' : 'inline-edit-form'">
      <div class="form-header">
        <h3>{{ showAddForm() ? "New Routine" : "Edit Routine" }}</h3>
        <div class="form-actions">
          <button
            mat-button
            (click)="showAddForm() ? onCancelAdd() : onCancelEdit()"
            [disabled]="showAddForm() ? addFormLoading() : editFormLoading()"
            class="cancel-button"
          >
            <span>Cancel</span>
          </button>
          <button
            mat-button
            (click)="showAddForm() ? onSaveAdd() : onSaveEdit()"
            [disabled]="
              (showAddForm() ? addFormLoading() : editFormLoading()) ||
              (showAddForm() ? addForm.invalid : editForm.invalid)
            "
            [class]="showAddForm() ? 'add-button' : 'save-button'"
          >
            @if (showAddForm() ? addFormLoading() : editFormLoading()) {
              <mat-spinner diameter="20"></mat-spinner>
            }
            @if (!(showAddForm() ? addFormLoading() : editFormLoading())) {
              <mat-icon>{{ showAddForm() ? "add" : "save" }}</mat-icon>
            }
            @if (!(showAddForm() ? addFormLoading() : editFormLoading())) {
              <span>{{ showAddForm() ? "Add" : "Save" }}</span>
            }
          </button>
        </div>
      </div>
      <form
        [formGroup]="showAddForm() ? addForm : editForm"
        (ngSubmit)="showAddForm() ? onSaveAdd() : onSaveEdit()"
      >
        <!-- Error Message -->
        @if (showAddForm() ? addFormError() : editFormError()) {
          <div [class]="showAddForm() ? 'add-form-error' : 'edit-form-error'">
            <mat-icon>error</mat-icon>
            <span>{{ showAddForm() ? addFormError() : editFormError() }}</span>
          </div>
        }
        <!-- Form Grid Layout - 2x3 -->
        <div class="form-grid">
          <!-- Row 1: Routine Name and Description -->
          <div class="field-container">
            <mat-form-field>
              <mat-label>Routine Name</mat-label>
              <input
                matInput
                formControlName="name"
                placeholder="Enter routine name"
              />
            </mat-form-field>
            @if (
              showAddForm()
                ? getAddFormFieldError("name")
                : getEditFormFieldError("name")
            ) {
              <div class="validation-error">
                {{
                  showAddForm()
                    ? getAddFormFieldError("name")
                    : getEditFormFieldError("name")
                }}
              </div>
            }
          </div>
          <div class="field-container">
            <mat-form-field>
              <mat-label>Description</mat-label>
              <input
                matInput
                formControlName="description"
                placeholder="Enter routine description"
              />
            </mat-form-field>
            @if (
              showAddForm()
                ? getAddFormFieldError("description")
                : getEditFormFieldError("description")
            ) {
              <div class="validation-error">
                {{
                  showAddForm()
                    ? getAddFormFieldError("description")
                    : getEditFormFieldError("description")
                }}
              </div>
            }
          </div>
          <!-- Row 2: Version (Read-only, shows selected version) and Runs As User -->
          <div class="field-container">
            <mat-form-field>
              <mat-label>Version</mat-label>
              <mat-select formControlName="versionId">
                @for (version of projectVersions(); track version.versionId) {
                  <mat-option [value]="version.versionId">
                    {{ getVersionDisplayName(version) }}
                  </mat-option>
                }
              </mat-select>
              <mat-hint>{{
                showEditForm()
                  ? "Version cannot be changed after creation"
                  : "Version is set from the selected version above"
              }}</mat-hint>
            </mat-form-field>
            @if (
              showAddForm()
                ? getAddFormFieldError("versionId")
                : getEditFormFieldError("versionId")
            ) {
              <div class="validation-error">
                {{
                  showAddForm()
                    ? getAddFormFieldError("versionId")
                    : getEditFormFieldError("versionId")
                }}
              </div>
            }
          </div>
          <div class="field-container">
            <mat-form-field>
              <mat-label>Runs As User</mat-label>
              <mat-select formControlName="runsAsId">
                @for (user of projectUsers(); track user.userId) {
                  <mat-option [value]="user.userId">
                    {{ user.userName }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (
              showAddForm()
                ? getAddFormFieldError("runsAsId")
                : getEditFormFieldError("runsAsId")
            ) {
              <div class="validation-error">
                {{
                  showAddForm()
                    ? getAddFormFieldError("runsAsId")
                    : getEditFormFieldError("runsAsId")
                }}
              </div>
            }
          </div>
          <!-- Row 3: Workflow and Schedule -->
          <div class="field-container">
            <mat-form-field>
              <mat-label>Workflow (Optional)</mat-label>
              <mat-select formControlName="workflowId">
                <mat-option [value]="null">Select workflow</mat-option>
                @for (notebook of projectNotebooks(); track notebook.workflowId) {
                  <mat-option [value]="notebook.workflowId">
                    <span class="workflow-option">
                      <span>{{ notebook.name }}</span>
                      @if (notebook.headlessOnly) {
                        <span class="routine-only-badge">Routine-only</span>
                      }
                    </span>
                  </mat-option>
                }
              </mat-select>
              <mat-hint
                >Choose a specific workflow from the current version</mat-hint
              >
            </mat-form-field>
          </div>
          <div class="field-container">
            <mat-form-field class="schedule-selector">
              <mat-label>Schedule</mat-label>
              <mat-select
                formControlName="cronScheduleType"
                (selectionChange)="
                  onCronScheduleChange(showAddForm() ? addForm : editForm, $event)
                "
              >
                @for (option of cronScheduleOptions; track option.value) {
                  <mat-option [value]="option.value">
                    <div class="schedule-option">
                      <span class="option-label">{{ option.label }}</span>
                      <span class="option-description">{{
                        option.description
                      }}</span>
                    </div>
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <!-- Custom cron input (shown when "Custom..." is selected) -->
            @if (showCustomCronInput()) {
              <div class="custom-cron-input">
                <mat-form-field class="w-full">
                  <mat-label>Custom Cron Expression</mat-label>
                  <input
                    matInput
                    formControlName="crontab"
                    placeholder="0 2 * * * (minute hour day month weekday)"
                    class="text-sm"
                  />
                  <mat-hint
                    >Format: minute hour day month weekday (e.g., "0 2 * * *"
                    for daily at 2 AM)</mat-hint
                  >
                </mat-form-field>
                @if (
                  (showAddForm() ? addForm : editForm).get("crontab")?.value &&
                  !validateCustomCron(
                    (showAddForm() ? addForm : editForm).get("crontab")?.value
                  )
                ) {
                  <div class="validation-error text-sm">
                    {{
                      getCronValidationError(
                        (showAddForm() ? addForm : editForm).get("crontab")?.value
                      )
                    }}
                  </div>
                }
                <!-- Cron format help -->
                <div class="cron-help-section">
                  <div class="help-header">
                    <mat-icon class="help-icon">help_outline</mat-icon>
                    <span class="help-title">Common Cron Examples:</span>
                  </div>
                  <div class="cron-examples">
                    @for (example of getCronFormatExamples(); track example.label) {
                      <div class="cron-example">
                        <code class="cron-code">{{ example.example }}</code>
                        <span class="cron-description">{{
                          example.description
                        }}</span>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
            <!-- Show selected schedule info -->
            @if (
              !showCustomCronInput() &&
              (showAddForm() ? addForm : editForm).get("crontab")?.value
            ) {
              <div class="selected-schedule-info">
                <span class="schedule-label">Selected:</span>
                {{
                  getSelectedCronScheduleLabel(showAddForm() ? addForm : editForm)
                }}
              </div>
            }
          </div>
        </div>
      </form>
    </div>
  }

  <mat-card
    class="border border-blue-100 rounded-lg shadow-sm overflow-hidden p-0"
  >
    @if (loading()) {
      <div class="flex justify-center items-center py-10">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
    }

    <!-- Empty State -->
    @if (
      !loading() &&
      dataSource.data.length === 0 &&
      !showVersionSelector() &&
      (selectedVersionId() || currentVersion || appStateService.version())
    ) {
      <div class="flex flex-col justify-center items-center py-16 gap-4">
        <mat-icon class="text-gray-400 text-6xl">schedule</mat-icon>
        <h3 class="text-gray-600 text-lg font-medium m-0">No routines found</h3>
        <p class="text-gray-500 text-sm m-0 text-center max-w-sm">
          Create automated routines to schedule your workflows.
        </p>
      </div>
    }

    @if (!loading() && dataSource.data.length > 0) {
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        <!-- Routine Name Column -->
        <ng-container matColumnDef="name">
          <th
            mat-header-cell
            *matHeaderCellDef
            mat-sort-header
            class="table-header"
          >
            Routine Name
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            {{ element.name }}
          </td>
        </ng-container>
        <!-- Description Column -->
        <ng-container matColumnDef="description">
          <th
            mat-header-cell
            *matHeaderCellDef
            mat-sort-header
            class="table-header"
          >
            Description
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            {{ element.description || "No description" }}
            @if (hasRoutineArguments(element)) {
              <span
                class="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                [matTooltip]="formatRoutineArguments(element)"
                matTooltipClass="routine-arguments-tooltip"
              >
                <mat-icon class="text-xs leading-none" inline>data_object</mat-icon>
                args
              </span>
            }
          </td>
        </ng-container>
        <!-- Runs As Column -->
        <ng-container matColumnDef="runsAsId">
          <th mat-header-cell *matHeaderCellDef class="table-header">
            Runs As
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            {{ getUserName(element.runsAsId) }}
          </td>
        </ng-container>
        <!-- Version Column -->
        <ng-container matColumnDef="version">
          <th mat-header-cell *matHeaderCellDef class="table-header">
            Version
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            <span
              class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
            >
              v{{ element.majorVersion }}.{{ element.minorVersion }}
            </span>
          </td>
        </ng-container>
        <!-- Schedule Column -->
        <ng-container matColumnDef="crontab">
          <th mat-header-cell *matHeaderCellDef class="table-header">
            Schedule
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            @if (element.crontab) {
              <span
                class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
              >
                {{ formatCronExpression(element.crontab) }}
              </span>
            } @else {
              <span class="text-gray-500 text-sm">Manual only</span>
            }
          </td>
        </ng-container>
        <!-- Paused Column -->
        <ng-container matColumnDef="paused">
          <th mat-header-cell *matHeaderCellDef class="table-header">Status</th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            @if (element.paused) {
              <span
                class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs flex items-center justify-center gap-1 status-badge"
              >
                <mat-icon class="text-xs leading-none">pause</mat-icon>
                <span class="leading-none">Paused</span>
              </span>
            } @else {
              <span
                class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center justify-center gap-1 status-badge"
              >
                <mat-icon class="text-xs leading-none">play_arrow</mat-icon>
                <span class="leading-none">Active</span>
              </span>
            }
          </td>
        </ng-container>
        <!-- Created Date Column -->
        <ng-container matColumnDef="createdAt">
          <th
            mat-header-cell
            *matHeaderCellDef
            mat-sort-header
            class="table-header"
          >
            Created
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            {{ element.createdAt | date: "short" }}
          </td>
        </ng-container>
        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-header-center">
            Actions
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell-center">
            <button
              mat-icon-button
              [matMenuTriggerFor]="planMenu"
              class="action-menu-button"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #planMenu="matMenu">
              <button mat-menu-item (click)="onActionMenu(element, 'run')">
                <mat-icon>play_arrow</mat-icon>
                <span>Run Now</span>
              </button>
              <mat-divider></mat-divider>
              <button
                mat-menu-item
                (click)="onActionMenu(element, 'delete')"
                class="text-red-600"
              >
                <mat-icon class="text-red-600">delete</mat-icon>
                <span>Delete</span>
              </button>
            </mat-menu>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="data-row"
        ></tr>
      </table>
    }
  </mat-card>
</div>
